import { zodResolver } from "@hookform/resolvers/zod";
import { type SetStateAction, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import useSWR from "swr";
import { simpleFetcher } from "~/api/fetcher";
import { type WorkflowStepInput, type WorkflowTemplate } from "~/api/types";
import {
  applyWorkflowTemplate,
  updateDocumentVersion,
} from "~/api/v2/requests";
import { useDocumentVersion } from "~/api/v2/useDocumentVersion";
import { useWorkflowTemplates } from "~/api/v2/useWorkflowTemplates";
import { PlatformRole } from "~/config/types";
import { canEditWorkflow } from "~/helpers/documentPermissions";
import { mapStepInputsToWorkflowSteps } from "~/helpers/workflow";
import { useUserContext } from "~/hooks/useUserContext";
import { useWorkflowUsers } from "~/hooks/useWorkflowUsers";
import {
  mapFormValuesToUpdateDocumentVersionData,
  mapDocumentVersionToFormValues,
  type WorkflowEditorFormValues,
  workflowEditorFormSchema,
} from "./workflowEditorForm";
import { type DocumentVersionDetails } from "~/api/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";

export type WorkflowEditorMode =
  | {
      type: "editing";
      documentVersion: DocumentVersionDetails;
      steps: WorkflowStepInput[];
    }
  | {
      type: "previewing";
      documentVersion: DocumentVersionDetails;
      steps: WorkflowStepInput[];
      description: string;
    };

export function useWorkflowEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const isWorkflowTemplatesEnabled = useFeatureFlagEnabled(
    featureFlags.workflowTemplates,
  );

  const canManagePlatform =
    user.role === PlatformRole.coordinator ||
    user.role === PlatformRole.superAdmin;

  const { data: documentVersion, isLoading, mutate } = useDocumentVersion(id);
  const { data: templates } = useWorkflowTemplates();
  const { hydrateSteps } = useWorkflowUsers();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [isApplying, setIsApplying] = useState(false);

  const templateDetailUrl = selectedTemplateId
    ? `/api/v2/workflow-templates/${selectedTemplateId}`
    : null;

  const { data: templateDetail } = useSWR<WorkflowTemplate>(
    templateDetailUrl,
    simpleFetcher,
  );

  const form = useForm<WorkflowEditorFormValues>({
    resolver: zodResolver(workflowEditorFormSchema),
    values: mapDocumentVersionToFormValues(documentVersion),
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const editedSteps = useWatch({
    control: form.control,
    name: "steps",
  });

  const isPreviewMode = selectedTemplateId != null && templateDetail != null;

  const previewSteps = templateDetail ? hydrateSteps(templateDetail) : [];

  const templateOptions =
    templates?.map((tmpl) => ({
      value: String(tmpl.id),
      label: tmpl.name ?? t(($) => $.workflowTemplates.defaultBadge),
    })) ?? [];

  const canEditCurrentWorkflow = documentVersion
    ? canEditWorkflow(documentVersion)
    : false;

  const showTemplateDropdown =
    !!documentVersion &&
    canEditCurrentWorkflow &&
    !documentVersion.isOngoing &&
    canManagePlatform &&
    isWorkflowTemplatesEnabled === true;

  const mode: WorkflowEditorMode | null = (() => {
    if (!documentVersion) return null;

    if (isPreviewMode) {
      return {
        type: "previewing" as const,
        documentVersion: {
          ...documentVersion,
          isOngoing: false,
          steps: mapStepInputsToWorkflowSteps(previewSteps),
          description: templateDetail.description,
          stepInstances: [],
        },
        steps: previewSteps,
        description: templateDetail?.description ?? "",
      };
    }

    return {
      type: "editing" as const,
      documentVersion: documentVersion,
      steps: editedSteps ?? [],
    };
  })();

  const hasUnsavedChanges = form.formState.isDirty;

  const setEditedSteps = (value: SetStateAction<WorkflowStepInput[]>) => {
    const current = form.getValues("steps");
    const nextValue = typeof value === "function" ? value(current) : value;

    form.setValue("steps", nextValue, { shouldDirty: true });
  };

  const handleSave = form.handleSubmit(
    async (values) => {
      if (!documentVersion) {
        return;
      }

      try {
        await updateDocumentVersion(
          documentVersion.id,
          mapFormValuesToUpdateDocumentVersionData(
            values,
            form.formState.dirtyFields,
          ),
        );
        form.reset(values);
        toast.success(t(($) => $.commons.workflow.workflowUpdatedSuccessfully));
        await mutate();
        void navigate(-1);
      } catch (error) {
        console.error(
          t(($) => $.commons.workflow.failedToUpdateWorkflow),
          error,
        );
        toast.error(t(($) => $.commons.workflow.failedToUpdateWorkflow));
      }
    },
    (errors) => {
      const firstError = Object.values(errors).find((error) => error?.message);
      if (firstError?.message) {
        toast.error(firstError.message);
      } else {
        toast.error(t(($) => $.toasts.error.generic));
      }
    },
  );

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId || !documentVersion) {
      return;
    }

    setIsApplying(true);
    try {
      await applyWorkflowTemplate(Number(selectedTemplateId), [
        documentVersion.id,
      ]);
      toast.success(t(($) => $.workflowTemplates.applyOnWorkflow.success));
      setSelectedTemplateId(null);

      const updatedDocumentVersion = await mutate();
      if (updatedDocumentVersion) {
        form.reset(mapDocumentVersionToFormValues(updatedDocumentVersion));
      }
    } catch (error) {
      console.error("Failed to apply template", error);
      toast.error(t(($) => $.workflowTemplates.applyOnWorkflow.error));
    } finally {
      setIsApplying(false);
    }
  };

  const handleTemplateSelect = (value: string) => {
    setSelectedTemplateId((current) => (value === current ? null : value));
  };

  return {
    isLoading,
    mode,
    selectedTemplateId,
    isApplying,
    showTemplateDropdown,
    templateOptions,
    form,
    hasUnsavedChanges,
    handleTemplateSelect,
    handleApplyTemplate,
    handleCancelPreview: () => setSelectedTemplateId(null),
    setEditedSteps,
    handleSave,
  };
}
